import React from 'react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

const HomeView = ({ session, setView, watches, savedArticles, inboxMatches }) => {
  const { language } = useLanguage();
  
  const watchCount = watches ? watches.length : 0;
  const savedCount = savedArticles ? savedArticles.length : 0;
  const inboxCount = inboxMatches ? inboxMatches.filter(item => !item.is_read).length : 0;
  const isNewUser = watchCount === 0;
  
  const userName = session?.user?.user_metadata?.full_name?.split(' ')[0] || t(language, 'home_default_user') || 'Researcher';

  return (
    <div className="max-w-6xl mx-auto pb-32 md:pb-10 px-4 md:px-0 mt-8 md:mt-12">
      
      {/* Välkomsthälsning */}
      <div className="mb-12 text-center md:text-left">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
          {t(language, 'home_welcome')}, {userName}! 👋
        </h1>
        <p className="text-xl text-slate-600 font-medium max-w-2xl">
          {isNewUser 
            ? t(language, 'home_new_user_title')
            : t(language, 'home_user_title')}
        </p>
      </div>

     {isNewUser ? (
        /* --- ONBOARDING FÖR NYA ANVÄNDARE --- */
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-slate-200 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 to-blue-600"></div>
          
          <span className="text-6xl mb-6 block">🤖</span>
          <h2 className="text-3xl font-bold text-slate-800 mb-4">{t(language, 'home_onboarding_heading')}</h2>
          <p className="text-lg text-slate-600 mb-10 max-w-xl mx-auto">
            {t(language, 'home_onboarding_desc')}
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-12 text-left">
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100">
              <span className="text-3xl mb-4 block">1️⃣</span>
              <h3 className="font-bold text-slate-800 mb-2">{t(language, 'home_onboarding_step1_title')}</h3>
              <p className="text-slate-600 text-sm">{t(language, 'home_onboarding_step1_desc')}</p>
            </div>
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100">
              <span className="text-3xl mb-4 block">2️⃣</span>
              <h3 className="font-bold text-slate-800 mb-2">{t(language, 'home_onboarding_step2_title')}</h3>
              <p className="text-slate-600 text-sm">{t(language, 'home_onboarding_step2_desc')}</p>
            </div>
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100">
              <span className="text-3xl mb-4 block">3️⃣</span>
              <h3 className="font-bold text-slate-800 mb-2">{t(language, 'home_onboarding_step3_title')}</h3>
              <p className="text-slate-600 text-sm">{t(language, 'home_onboarding_step3_desc')}</p>
            </div>
          </div>

          <button 
            onClick={() => setView('topics')}
            className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-blue-700 transition-all shadow-[0_4px_14px_rgba(37,99,235,0.39)] hover:-translate-y-0.5 active:scale-95 min-h-[56px] flex items-center justify-center mx-auto"
          >
            <span className="mr-2 text-xl">🎯</span> {t(language, 'home_onboarding_btn_create')}
          </button>
        </div>

      ) : (
        /* --- DASHBOARD FÖR ÅTERKOMMANDE ANVÄNDARE --- */
        <div className="space-y-6">
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            
            {/* INKORG */}
            <div 
              onClick={() => setView('inbox')}
              className="bg-white p-7 md:p-8 rounded-3xl border border-slate-200 shadow-sm cursor-pointer hover:border-blue-300 hover:shadow-md transition-all group relative overflow-hidden flex flex-col justify-between min-h-[160px]"
            >
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity text-6xl">📬</div>
              <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">{t(language, 'home_stat_inbox')}</p>
              
              <div className="flex items-center">
                {inboxCount > 0 ? (
                  <div className="flex items-center">
                    <span className="bg-red-500 text-white font-bold text-2xl h-12 min-w-[48px] px-4 rounded-full flex items-center justify-center shadow-sm border-2 border-white ring-2 ring-red-100">
                      {inboxCount}
                    </span>
                    <span className="ml-3 text-slate-700 font-bold">{t(language, 'home_stat_articles')}</span>
                  </div>
                ) : (
                  <div className="flex items-end">
                    <span className="text-4xl font-bold text-slate-300">0</span>
                    <span className="ml-2 text-slate-500 mb-1 font-medium">{t(language, 'home_stat_unread')}</span>
                  </div>
                )}
              </div>
              
              {inboxCount > 0 && <div className="mt-5 text-sm text-blue-600 font-bold group-hover:underline flex items-center">{t(language, 'home_action_read')} <span className="ml-1">→</span></div>}
            </div>

            {/* ASSISTENTER */}
            <div 
              onClick={() => setView('watches')}
              className="bg-white p-7 md:p-8 rounded-3xl border border-slate-200 shadow-sm cursor-pointer hover:border-green-300 hover:shadow-md transition-all group relative overflow-hidden flex flex-col justify-between min-h-[160px]"
            >
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity text-6xl">🤖</div>
              <div className="flex items-center mb-4">
                <span className="relative flex h-3 w-3 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <p className="text-sm font-bold text-slate-500 uppercase tracking-wider">{t(language, 'home_stat_assistants')}</p>
              </div>
              <div className="flex items-end">
                <span className="text-4xl font-bold text-slate-800">{watchCount}</span>
                <span className="ml-2 text-slate-600 mb-1 font-medium">{t(language, 'home_stat_active')}</span>
              </div>
              <div className="mt-5 text-sm text-green-600 font-bold group-hover:underline flex items-center">{t(language, 'home_action_manage')} <span className="ml-1">→</span></div>
            </div>

            {/* BIBLIOTEK */}
            <div 
              onClick={() => setView('saved')}
              className="bg-white p-7 md:p-8 rounded-3xl border border-slate-200 shadow-sm cursor-pointer hover:border-amber-300 hover:shadow-md transition-all group relative overflow-hidden flex flex-col justify-between min-h-[160px]"
            >
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity text-6xl">📚</div>
              <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">{t(language, 'home_stat_library')}</p>
              <div className="flex items-end">
                <span className="text-4xl font-bold text-slate-800">{savedCount}</span>
                <span className="ml-2 text-slate-600 mb-1 font-medium">{t(language, 'home_stat_saved')}</span>
              </div>
              <div className="mt-5 text-sm text-amber-600 font-bold group-hover:underline flex items-center">{t(language, 'home_action_open')} <span className="ml-1">→</span></div>
            </div>

          </div>

          {/* Quick Action */}
          <div className="mt-8 bg-gradient-to-r from-slate-800 to-slate-900 rounded-3xl p-8 md:p-10 text-white flex flex-col md:flex-row items-center justify-between shadow-lg">
            <div className="mb-6 md:mb-0 text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2">{t(language, 'home_quick_title')}</h3>
              <p className="text-slate-300 max-w-xl font-medium">{t(language, 'home_quick_desc')}</p>
            </div>
            <button 
              onClick={() => setView('topics')}
              className="bg-white text-slate-900 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-blue-50 transition-all w-full md:w-auto shadow-sm min-h-[56px] active:scale-95 flex items-center justify-center shrink-0 ml-0 md:ml-6"
            >
              <span className="mr-2 text-xl">➕</span> {t(language, 'home_btn_new')}
            </button>
          </div>

        </div>
      )}

    </div>
  );
};

export default HomeView;